import { mockSuccess } from './shared';
import type { MockRouteEntry } from '../mock-api.types';

type Ticket = {
    id: string;
    reference: string;
    subject: string;
    status: string;
    priority: string;
    source: string;
    handler?: string;
    messages_count?: number;
    created_at?: string;
};

type Message = {
    id: string;
    direction: 'in' | 'out' | 'internal';
    body_text: string;
    vetting_status: string;
    created_at?: string;
};

let SUPPORT_SETTINGS = {
    sla_hours: 24,
    default_priority: 'normal',
    chatbot_enabled: true,
    business_hours: 'Mon–Fri 09:00–17:00',
};

const TICKETS: Ticket[] = [
    {
        id: 'tkt-1',
        reference: 'SUP-1001',
        subject: 'Cannot export DMS PDF',
        status: 'open',
        priority: 'normal',
        source: 'web',
        handler: 'tenant',
        messages_count: 2,
        created_at: '2026-06-04T09:00:00Z',
    },
    {
        id: 'tkt-2',
        reference: 'SUP-1002',
        subject: 'Billing invoice question',
        status: 'pending_vetting',
        priority: 'low',
        source: 'email',
        handler: 'tenant',
        messages_count: 1,
        created_at: '2026-06-03T14:00:00Z',
    },
    {
        id: 'tkt-3',
        reference: 'SUP-1003',
        subject: 'Mailing calendar sync',
        status: 'open',
        priority: 'high',
        source: 'chat',
        handler: 'tenant',
        messages_count: 1,
        created_at: '2026-06-04T08:00:00Z',
    },
];

const MESSAGES: Record<string, Message[]> = {
    'tkt-1': [
        { id: 'm-1', direction: 'in', body_text: 'Export button spins forever.', vetting_status: 'sent', created_at: '2026-06-04T09:00:00Z' },
        { id: 'm-2', direction: 'out', body_text: 'We are checking the mock PDF endpoint.', vetting_status: 'sent', created_at: '2026-06-04T09:30:00Z' },
    ],
    'tkt-3': [
        { id: 'm-4', direction: 'in', body_text: 'Can we see team meetings in mailing calendar?', vetting_status: 'sent', created_at: '2026-06-04T08:00:00Z' },
    ],
    'tkt-2': [
        { id: 'm-3', direction: 'in', body_text: 'Please confirm invoice #4402.', vetting_status: 'pending', created_at: '2026-06-03T14:00:00Z' },
    ],
};

function ticketId(url: string): string | undefined {
    const m = url.match(/\/tickets\/([^/]+)/i);
    return m?.[1];
}

export const SUPPORT_MOCK_HANDLERS: MockRouteEntry[] = [
    { method: 'GET', pattern: /\/api\/[^/]+\/[^/]+\/support\/settings/i, handler: () => mockSuccess(SUPPORT_SETTINGS) },
    {
        method: 'PATCH',
        pattern: /\/api\/[^/]+\/[^/]+\/support\/settings/i,
        handler: (req) => {
            SUPPORT_SETTINGS = { ...SUPPORT_SETTINGS, ...(req.body as object) };
            return mockSuccess(SUPPORT_SETTINGS);
        },
    },

    {
        method: 'GET',
        pattern: /\/api\/[^/]+\/[^/]+\/support\/tickets(\?|$)/i,
        handler: () => mockSuccess({ data: TICKETS }),
    },
    {
        method: 'GET',
        pattern: /\/api\/[^/]+\/[^/]+\/support\/tickets\/[^/?]+/i,
        handler: (req) => {
            const id = ticketId(req.url) ?? TICKETS[0].id;
            const ticket = TICKETS.find((t) => t.id === id) ?? TICKETS[0];
            return mockSuccess({ ticket, messages: MESSAGES[id] ?? [] });
        },
    },
    {
        method: 'POST',
        pattern: /\/api\/[^/]+\/[^/]+\/support\/tickets\/[^/]+\/messages/i,
        handler: (req) => {
            const id = ticketId(req.url) ?? TICKETS[0].id;
            const body = (req.body || {}) as { body?: string; internal?: boolean };
            const msg: Message = {
                id: `m-${Date.now()}`,
                direction: body.internal ? 'internal' : 'out',
                body_text: body.body || '',
                vetting_status: 'sent',
                created_at: new Date().toISOString(),
            };
            MESSAGES[id] = [...(MESSAGES[id] ?? []), msg];
            return mockSuccess(msg);
        },
    },
    {
        method: 'POST',
        pattern: /\/api\/[^/]+\/[^/]+\/support\/tickets\/[^/]+\/escalate-to-ashcorp/i,
        handler: (req) => {
            const id = ticketId(req.url) ?? TICKETS[0].id;
            const t = TICKETS.find((x) => x.id === id);
            if (t) {
                t.status = 'escalated';
                t.handler = 'ashcorp';
            }
            return mockSuccess(t ?? TICKETS[0]);
        },
    },
    {
        method: 'POST',
        pattern: /\/api\/[^/]+\/[^/]+\/support\/tickets(\?|$)/i,
        handler: (req) => {
            const body = (req.body || {}) as { subject?: string; body?: string };
            const next: Ticket = {
                id: `tkt-${TICKETS.length + 1}`,
                reference: `SUP-${1000 + TICKETS.length + 1}`,
                subject: body.subject || 'New ticket',
                status: 'open',
                priority: 'normal',
                source: 'web',
                handler: 'tenant',
                messages_count: 1,
                created_at: new Date().toISOString(),
            };
            TICKETS.push(next);
            MESSAGES[next.id] = [
                { id: 'm-new', direction: 'in', body_text: body.body || '', vetting_status: 'sent', created_at: next.created_at },
            ];
            return mockSuccess(next);
        },
    },
];
